/**
 * Decide whether a session-state observation is worth persisting to `session-events`.
 *
 * WHY THIS EXISTS. Measured 2026-08-04, the collection was taking **~504,000 documents/day**
 * of state snapshots against **~2,000/day** of real lifecycle events (boot, unpaired,
 * crash_dump_captured, graceful_shutdown) — a 250:1 noise ratio, 2.98M of 3.00M docs. The
 * signal is the 2k; the 504k is a periodic poll re-reporting a steady CONNECTED state.
 *
 * ROOT CAUSE. SessionEventService deduped by comparing against the last entry of its own
 * in-memory buffer, and `flushSessionEventBuffer()` sets that buffer to `[]`. After every
 * flush `lastIndex < 0`, so the next observation was written unconditionally regardless of
 * whether anything had changed. The content script is also torn down and remounted
 * constantly on WhatsApp Web, which reset the counter that gates immediate flushing — so in
 * practice almost every poll became a document.
 *
 * THE FIX is to dedupe on a small SIGNATURE that is persisted independently of the buffer.
 * A signature survives both the flush and the remount; the buffer survives neither.
 *
 * Debugging observability lives in Loki. This collection exists for RETROSPECTION — asking
 * months later what happened to a customer's session — so it should hold transitions, not
 * polls.
 */

/** The fields that make one observation meaningfully different from another. */
const MEANINGFUL_FIELDS = ['state', 'stream', 'hasSynced', 'status'] as const;

export interface SessionStateObservation {
  state?: unknown;
  stream?: unknown;
  hasSynced?: unknown;
  status?: unknown;
  triggerdBy?: unknown;
  [k: string]: unknown;
}

/**
 * A compact, stable key for the meaningful fields of an observation.
 *
 * Deliberately ignores `triggerdBy`, `qrcode`, `podId` and the timestamps: which subsystem
 * noticed a state, and when, does not make it a different state. Absent and empty-string are
 * kept distinct (` ` sentinel) so a real `'' -> undefined` transition is never swallowed.
 */
export function signatureOf(ev: SessionStateObservation): string {
  return MEANINGFUL_FIELDS
    .map((f) => (ev[f] === undefined ? ' ' : String(ev[f])))
    .join('|');
}

/**
 * True when `next` should be written to `session-events`.
 *
 * `lastSignature` is the signature of the last observation actually persisted for this
 * session — null/undefined when nothing has been persisted yet. It MUST be stored somewhere
 * that outlives the buffer (localStorage), or this degrades back to the bug above.
 *
 * An observation carrying none of the meaningful fields is a liveness ping, not an event,
 * and is never persisted however long it has been since the last write.
 */
export function shouldPersistSessionEvent(
  lastSignature: string | null | undefined,
  next: SessionStateObservation,
): boolean {
  const carriesState = MEANINGFUL_FIELDS.some((f) => next[f] !== undefined);
  if (!carriesState) return false;
  const sig = signatureOf(next);
  return !lastSignature || sig !== lastSignature;
}
